import { minifySettings } from './profiles';
import { t } from '../i18n';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface SpiffsEntry {
  name: string;
  path: string;
  size: number;
  isDir: boolean;
}

type DataHandler = (chunk: string) => void;

export class SerialSession {
  port: SerialPort | null = null;
  connected = false;
  log = '';

  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private reading = false;
  private decoder = new TextDecoder();
  private listeners = new Set<DataHandler>();
  private waiters: Array<{
    pred: (text: string) => boolean;
    resolve: (text: string) => void;
    timer: number;
  }> = [];

  onData(handler: DataHandler): () => void {
    this.listeners.add(handler);
    return () => this.listeners.delete(handler);
  }

  private emit(chunk: string) {
    this.log = (this.log + chunk).slice(-200000);
    this.listeners.forEach((handler) => handler(chunk));
    const pending = [...this.waiters];
    for (const waiter of pending) {
      if (waiter.pred(this.log)) {
        window.clearTimeout(waiter.timer);
        this.waiters = this.waiters.filter((item) => item !== waiter);
        waiter.resolve(this.log);
      }
    }
  }

  waitFor(pred: (text: string) => boolean, timeoutMs: number): Promise<string> {
    if (pred(this.log)) {
      return Promise.resolve(this.log);
    }
    return new Promise((resolve, reject) => {
      const entry = {
        pred,
        resolve,
        timer: window.setTimeout(() => {
          this.waiters = this.waiters.filter((item) => item !== entry);
          reject(new Error(`Timed out waiting for serial output (${timeoutMs}ms)`));
        }, timeoutMs),
      };
      this.waiters.push(entry);
    });
  }

  async attach(port: SerialPort): Promise<void> {
    await this.detach();
    this.port = port;
    this.connected = true;
    this.reading = true;
    this.pump();
  }

  async open(port: SerialPort, baudRate = 115200): Promise<void> {
    try {
      await port.close();
    } catch {
      // already closed
    }
    await port.open({ baudRate, bufferSize: 8192 });
    try {
      await port.setSignals?.({ dataTerminalReady: false, requestToSend: false });
    } catch {
      // some adapters ignore this
    }
    await this.attach(port);
  }

  async detach(): Promise<void> {
    this.reading = false;
    this.connected = false;
    for (const waiter of this.waiters) {
      window.clearTimeout(waiter.timer);
    }
    this.waiters = [];
    try {
      this.reader?.releaseLock();
    } catch {
      // ignore
    }
    this.reader = null;
    this.port = null;
  }

  async stealPort(): Promise<SerialPort | null> {
    const port = this.port;
    this.reading = false;
    this.connected = false;
    for (const waiter of this.waiters) {
      window.clearTimeout(waiter.timer);
    }
    this.waiters = [];
    try {
      this.reader?.releaseLock();
    } catch {
      // ignore
    }
    this.reader = null;
    this.port = null;
    if (port) {
      try {
        await port.close();
      } catch {
        // esptool will reopen
      }
    }
    return port;
  }

  async close(): Promise<void> {
    await this.stealPort();
  }

  private async pump() {
    if (!this.port?.readable) {
      return;
    }
    this.reader = this.port.readable.getReader();
    try {
      while (this.reading && this.reader) {
        const { value, done } = await this.reader.read();
        if (done) {
          break;
        }
        if (value) {
          this.emit(this.decoder.decode(value, { stream: true }));
        }
      }
    } catch {
      // port closed or reset
    } finally {
      try {
        this.reader?.releaseLock();
      } catch {
        // ignore
      }
      this.reader = null;
    }
  }

  async sendLine(line: string): Promise<void> {
    if (!this.port?.writable) {
      throw new Error(t('usb.notWritable'));
    }
    const writer = this.port.writable.getWriter();
    try {
      await writer.write(new TextEncoder().encode(`${line}\n`));
    } finally {
      writer.releaseLock();
    }
  }

  async command(line: string, done: RegExp, timeoutMs = 8000): Promise<string> {
    const before = this.log.length;
    await this.sendLine(line);
    await this.waitFor((all) => done.test(all.slice(before)), timeoutMs);
    await sleep(200);
    return this.log.slice(before);
  }

  async listFiles(dir = '/'): Promise<SpiffsEntry[]> {
    const output = await this.command(`ls ${dir}`, /Files in |Failed to open|Not a directory/, 8000);
    const files: SpiffsEntry[] = [];
    const seen = new Set<string>();
    for (const raw of output.split(/\r?\n/)) {
      const line = raw.trim();
      const dirMatch = line.match(/\[DIR\]\s+(\S+)/i);
      if (dirMatch) {
        const name = dirMatch[1].replace(/^\//, '');
        const path = `/${name}`;
        if (!seen.has(path)) {
          seen.add(path);
          files.push({ name, path, size: 0, isDir: true });
        }
        continue;
      }
      const fileMatch = line.match(/(\S+)\s+\((\d+)\s+bytes\)/i);
      if (fileMatch) {
        const name = fileMatch[1].replace(/^\//, '');
        const path = `/${name}`;
        if (!seen.has(path)) {
          seen.add(path);
          files.push({ name, path, size: Number(fileMatch[2]), isDir: false });
        }
      }
    }
    return files;
  }

  async readFile(path: string): Promise<string> {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    const output = await this.command(
      `cat ${normalized}`,
      /=== End of file ===|Failed to read file|File not found/,
      12000,
    );
    const start = output.indexOf('=== Contents of');
    const end = output.indexOf('=== End of file ===');
    if (start >= 0 && end > start) {
      const nl = output.indexOf('\n', start);
      return output.slice(nl + 1, end).replace(/^\r?\n/, '').replace(/\r?\n$/, '');
    }
    throw new Error(output.trim().split(/\r?\n/).pop() || t('usb.readFailed', { path: normalized }));
  }

  async writeFile(path: string, content: string): Promise<void> {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    let payload = content;
    try {
      payload = minifySettings(content);
    } catch {
      payload = content.replace(/\r?\n/g, ' ');
    }
    const output = await this.command(
      `write ${normalized} ${payload}`,
      /Wrote \/|Failed to write|Invalid path|write requires content|Unknown command/,
      20000,
    );
    if (/Unknown command:\s*write/i.test(output)) {
      throw new Error(t('usb.noWriteCommand'));
    }
    if (!new RegExp(`Wrote ${normalized.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i').test(output)) {
      throw new Error(output.trim().split(/\r?\n/).pop() || t('usb.writeFailed', { path: normalized }));
    }
  }

  async removeFile(path: string): Promise<void> {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    const output = await this.command(
      `rm ${normalized}`,
      /File deleted|Failed to delete/,
      8000,
    );
    if (!/File deleted/i.test(output)) {
      throw new Error(output.trim().split(/\r?\n/).pop() || t('usb.deleteFailed', { path: normalized }));
    }
  }

  async waitUntilReady(timeoutMs = 20000): Promise<void> {
    try {
      await this.waitFor((text) => /Command system initialized/i.test(text), Math.min(timeoutMs, 12000));
      await sleep(400);
      return;
    } catch {
      // already running, or we missed the boot banner
    }
    try {
      await this.sendLine('help');
      await this.waitFor((text) => /Available commands/i.test(text), 5000);
    } catch {
      await sleep(400);
    }
  }
}

export function isMacConfigName(name: string): boolean {
  return /^[0-9A-Fa-f]{8,16}\.json$/i.test(name.replace(/^\//, ''));
}

export async function removeMacConfigFiles(session: SerialSession): Promise<string[]> {
  const files = await session.listFiles('/');
  const removed: string[] = [];
  for (const file of files) {
    if (isMacConfigName(file.name)) {
      await session.removeFile(file.path);
      removed.push(file.path);
    }
  }
  return removed;
}

export async function provisionFilesOverSerial(
  session: SerialSession,
  settingsJson: string,
): Promise<void> {
  await session.waitUntilReady();
  // Drop leftover MAC configs first so they cannot win a reboot on old firmware.
  await removeMacConfigFiles(session);
  await session.writeFile('/settings.json', settingsJson);
  // write + applyJson/save() mirrors settings to the MAC file again — delete it.
  await removeMacConfigFiles(session);
}

export { sleep as serialSleep };
